import { useMemo } from 'react';
import * as THREE from 'three';

export function useParticleTemplates() {
  const createTemplate = useMemo(() => {
    return (templateName, currentColor) => {
      const templates = {
        hearts: () => {
          const count = 2000;
          const positions = new Float32Array(count * 3);
          const colors = new Float32Array(count * 3);

          for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            const t = (i / count) * Math.PI * 2;
            const x = 16 * Math.pow(Math.sin(t), 3);
            const y = -(13 * Math.cos(t) - 5 * Math.cos(2*t) - 2 * Math.cos(3*t) - Math.cos(4*t));

            positions[i3] = x * 0.05 + (Math.random() - 0.5) * 0.2;
            positions[i3 + 1] = y * 0.05 + (Math.random() - 0.5) * 0.2;
            positions[i3 + 2] = (Math.random() - 0.5) * 2;

            colors[i3] = currentColor.r;
            colors[i3 + 1] = currentColor.g;
            colors[i3 + 2] = currentColor.b;
          }
          return { positions, colors, count };
        },

        flowers: () => {
          const count = 2500;
          const positions = new Float32Array(count * 3);
          const colors = new Float32Array(count * 3);

          for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            const angle = (i / count) * Math.PI * 2 * 5;
            const radius = 0.3 + Math.random() * 1.5;
            const r = radius * (1 + 0.3 * Math.sin(angle * 3));
            const x = r * Math.cos(angle);
            const y = r * Math.sin(angle);

            positions[i3] = x + (Math.random() - 0.5) * 0.3;
            positions[i3 + 1] = y + (Math.random() - 0.5) * 0.3;
            positions[i3 + 2] = (Math.random() - 0.5) * 2;

            colors[i3] = currentColor.r;
            colors[i3 + 1] = currentColor.g;
            colors[i3 + 2] = currentColor.b;
          }
          return { positions, colors, count };
        },

        saturn: () => {
          const count = 3000;
          const positions = new Float32Array(count * 3);
          const colors = new Float32Array(count * 3);

          for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            if (i < count * 0.7) {
              const phi = Math.acos(2 * Math.random() - 1);
              const theta = Math.random() * Math.PI * 2;
              const r = 0.5 + Math.random() * 0.5;
              positions[i3] = r * Math.sin(phi) * Math.cos(theta);
              positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
              positions[i3 + 2] = r * Math.cos(phi);
            } else {
              const angle = (i / count) * Math.PI * 2;
              const ringRadius = 1.2 + (i / count) * 0.8;
              positions[i3] = ringRadius * Math.cos(angle);
              positions[i3 + 1] = (Math.random() - 0.5) * 0.2;
              positions[i3 + 2] = ringRadius * Math.sin(angle);
            }
            colors[i3] = currentColor.r;
            colors[i3 + 1] = currentColor.g;
            colors[i3 + 2] = currentColor.b;
          }
          return { positions, colors, count };
        },

        buddha: () => {
          const count = 2800;
          const positions = new Float32Array(count * 3);
          const colors = new Float32Array(count * 3);

          for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            const t = i / count;
            let x, y, z;

            if (t < 0.3) {
              const angle = (i / (count * 0.3)) * Math.PI * 2;
              const radius = 0.4;
              x = radius * Math.cos(angle);
              y = 1.2 + radius * Math.sin(angle);
              z = (Math.random() - 0.5) * 0.3;
            } else if (t < 0.6) {
              const angle = (i / count) * Math.PI * 2;
              const radius = 0.3 + (t - 0.3) * 0.2;
              x = radius * Math.cos(angle);
              y = 0.8 - (t - 0.3) * 1.2;
              z = (Math.random() - 0.5) * 0.4;
            } else {
              const angle = (i / count) * Math.PI * 2;
              const radius = 0.5;
              x = radius * Math.cos(angle);
              y = -0.4 - (t - 0.6) * 0.3;
              z = (Math.random() - 0.5) * 0.5;
            }

            positions[i3] = x + (Math.random() - 0.5) * 0.1;
            positions[i3 + 1] = y + (Math.random() - 0.5) * 0.1;
            positions[i3 + 2] = z;

            colors[i3] = currentColor.r;
            colors[i3 + 1] = currentColor.g;
            colors[i3 + 2] = currentColor.b;
          }
          return { positions, colors, count };
        },

        fireworks: () => {
          const count = 3500;
          const positions = new Float32Array(count * 3);
          const colors = new Float32Array(count * 3);

          for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            const angle = (i / count) * Math.PI * 2 * 8;
            const radius = Math.random() * 2;
            const x = radius * Math.cos(angle) * Math.cos(angle * 2);
            const y = radius * Math.sin(angle) * Math.cos(angle * 2);
            const z = (Math.random() - 0.5) * 2;

            positions[i3] = x;
            positions[i3 + 1] = y;
            positions[i3 + 2] = z;

            const hue = (i / count) * 360;
            const tempColor = new THREE.Color().setHSL(hue / 360, 0.8, 0.6);
            colors[i3] = tempColor.r;
            colors[i3 + 1] = tempColor.g;
            colors[i3 + 2] = tempColor.b;
          }
          return { positions, colors, count };
        }
      };

      return templates[templateName]();
    };
  }, []);

  return { createTemplate };
}

